import React from 'react';
import {
  Grid,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent
} from '@mui/material';
import { Features } from '@/types/company-config';

interface FeaturesTabProps {
  data: Features;
  onChange: (field: string, value: unknown) => void;
  readOnly?: boolean;
}

const FeaturesTab: React.FC<FeaturesTabProps> = ({ data, onChange, readOnly = false }) => {
  const handleFeatureChange = (section: string, field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(section, {
      ...data[section as keyof Features],
      [field]: event.target.checked
    });
  };

  const handleExportFormatsChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    onChange('reporting', {
      ...data.reporting,
      exportFormats: value
    });
  };

  const exportFormatOptions = ['pdf', 'excel', 'csv', 'json'];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Platform Features
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Enable or disable features and configure their behavior across the platform.
      </Typography>

      <Grid container spacing={3}>
        {/* Client Management */}
        <Grid size={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Client Management
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.clientManagement?.enabled ?? true}
                      onChange={handleFeatureChange('clientManagement', 'enabled')}
                      disabled={readOnly}
                    />
                  }
                  label="Enable Client Management Module"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.clientManagement?.autoAssignment ?? false}
                      onChange={handleFeatureChange('clientManagement', 'autoAssignment')}
                      disabled={readOnly || !data.clientManagement?.enabled}
                    />
                  }
                  label="Auto-assign clients to lenders"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.clientManagement?.duplicateDetection ?? true}
                      onChange={handleFeatureChange('clientManagement', 'duplicateDetection')}
                      disabled={readOnly || !data.clientManagement?.enabled}
                    />
                  }
                  label="Enable duplicate detection"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Loan Management */}
        <Grid size={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Loan Management
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.loanManagement?.enabled ?? true}
                      onChange={handleFeatureChange('loanManagement', 'enabled')}
                      disabled={readOnly}
                    />
                  }
                  label="Enable Loan Management Module"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.loanManagement?.autoUnderwriting ?? false}
                      onChange={handleFeatureChange('loanManagement', 'autoUnderwriting')}
                      disabled={readOnly || !data.loanManagement?.enabled}
                    />
                  }
                  label="Enable automated underwriting"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.loanManagement?.creditCheckIntegration ?? false}
                      onChange={handleFeatureChange('loanManagement', 'creditCheckIntegration')}
                      disabled={readOnly || !data.loanManagement?.enabled}
                    />
                  }
                  label="Enable credit check integrations"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Reporting */}
        <Grid size={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Reporting & Analytics
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.reporting?.enabled ?? true}
                      onChange={handleFeatureChange('reporting', 'enabled')}
                      disabled={readOnly}
                    />
                  }
                  label="Enable Reporting Module"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.reporting?.scheduledReports ?? false}
                      onChange={handleFeatureChange('reporting', 'scheduledReports')}
                      disabled={readOnly || !data.reporting?.enabled}
                    />
                  }
                  label="Enable scheduled reports"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth disabled={readOnly || !data.reporting?.enabled}>
                  <InputLabel>Export Formats</InputLabel>
                  <Select
                    multiple
                    value={data.reporting?.exportFormats || ['pdf', 'excel', 'csv']}
                    onChange={handleExportFormatsChange}
                    input={<OutlinedInput label="Export Formats" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value.toUpperCase()} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {exportFormatOptions.map((format) => (
                      <MenuItem key={format} value={format}>
                        {format.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Notifications */}
        <Grid size={12}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notifications
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.notifications?.email ?? true}
                      onChange={handleFeatureChange('notifications', 'email')}
                      disabled={readOnly}
                    />
                  }
                  label="Email notifications"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.notifications?.sms ?? false}
                      onChange={handleFeatureChange('notifications', 'sms')}
                      disabled={readOnly}
                    />
                  }
                  label="SMS notifications"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.notifications?.push ?? true}
                      onChange={handleFeatureChange('notifications', 'push')}
                      disabled={readOnly}
                    />
                  }
                  label="Push notifications"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.notifications?.webhooks ?? false}
                      onChange={handleFeatureChange('notifications', 'webhooks')}
                      disabled={readOnly}
                    />
                  }
                  label="Webhook notifications"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FeaturesTab;